import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ContentLoader from 'react-content-loader';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-hot-toast';
import { Link, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

import { ReactComponent as CopyIcon } from '../../../assets/images/copy.svg';
import NavHeader from '../../../components/common/NavHeader';
import ImageControl from '../../../components/common/settings/ImageControl';
import ShortToast from '../../../components/common/toasts/ShortToast';
import { NAVIGATE_PATH } from '../../../helpers/constants';
import { useAppSelector } from '../../../hooks/hooks';
import { getCompany, updateSamlConfig } from '../../../requests';
import { UserRole } from '../../../models/IUser';
import { ButtonRegular } from '../../../components/common/buttons/ButtonRegular';

const CompanySettings = () => {
  const { user, isManager } = useAppSelector((state) => state.userReducer);
  const queryClient = useQueryClient();

  const { data: company, isLoading } = useQuery({
    queryKey: ['companyDetails'],
    queryFn: getCompany,
  });

  const [editingSaml, setEditingSaml] = useState(false);
  const [samlConfig, setSamlConfig] = useState({
    enableSaml: false,
    enforceSaml: false,
    idpMetadataUrl: '',
    idpMetadataXml: '',
  });

  useEffect(() => {
    if (company && !editingSaml) {
      setSamlConfig({
        enableSaml: company.enableSaml || false,
        enforceSaml: company.enforceSaml || false,
        idpMetadataUrl: company.idpMetadataUrl || '',
        idpMetadataXml: company.idpMetadataXml || '',
      });
    }
  }, [company, editingSaml]);

  // Mutation for updating SAML config
  const samlMutation = useMutation({
    mutationFn: updateSamlConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyDetails'] });
      setEditingSaml(false);
      toast.custom(
        <ShortToast text="SAML configuration updated successfully" />,
      );
    },
    onError: (error) => {
      console.error('Error updating SAML config:', error);
      toast.custom(<ShortToast text="Failed to update SAML configuration" />);
    },
  });

  const handleCopy = () => {
    toast.custom(
      <ShortToast
        text="Copied to clipboard"
        icon={<CopyIcon fill="#223BF4" />}
      />,
    );
  };

  if (user?.individual) {
    return <Navigate to={NAVIGATE_PATH.profileSettings} replace />;
  }

  return (
    <>
      <NavHeader label={`Company ${isManager ? 'Settings' : 'Info'}`} />
      <div className="main-container w-full pt-4 gap-2 max-w-[54rem] ">
        {isManager && (
          <div className="pb-3 font-medium text-regular text-core-dark">
            Manage your organization settings below
          </div>
        )}
        <div className="settings-header">{user?.company.name}</div>
        <table className="w-full settings-table">
          <tbody>
            <tr>
              <td>Company Logo</td>
              <td>
                <ImageControl
                  src={user?.company.photo}
                  type={'company'}
                  editable={isManager}
                />
              </td>
            </tr>
            <tr>
              <td>Company Name</td>
              <td className="w-full text-black rounded bg-grey-50">
                {user?.company.name}
              </td>
            </tr>
            {(user?.role === UserRole.MANAGER ||
              user?.role === UserRole.ADMIN) && (
              <tr>
                <td>Admin Portal</td>
                <td className="w-full">
                  <a
                    href="https://www.learnitadminportal.com/s/login"
                    className="underline-link"
                    target="_blank"
                  >
                    Log in to Admin Portal
                  </a>
                </td>
              </tr>
            )}

            {!isLoading && (
              <>
                <tr>
                  <td>Total members</td>
                  <td className="w-full">
                    <div className="flex justify-between">
                      <span>{company?.totalMembers}</span>
                      <Link
                        className="hidden underline-link"
                        to={NAVIGATE_PATH.teamMembers}
                      >
                        View members
                      </Link>
                    </div>
                  </td>
                </tr>
                {company?.inviteLink && (
                  <tr>
                    <td>Team invite url</td>
                    <td className="w-full">
                      <div className="flex items-center justify-between gap-4 text-grey-700">
                        <div>{company?.inviteLink}</div>
                        <div className="cursor-pointer">
                          <CopyToClipboard
                            text={company?.inviteLink ?? ''}
                            onCopy={() => handleCopy()}
                          >
                            <CopyIcon
                              width="1.5rem"
                              height="1.5rem"
                              fill="#4A4A68"
                            />
                          </CopyToClipboard>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {company?.primaryContact && (
                  <>
                    <tr className="border-t border-grey-100"></tr>
                    <tr>
                      <td>Company Contact</td>
                      <td className="text-black">
                        {company.primaryContact.name}
                      </td>
                    </tr>
                    <tr>
                      <td>Contact Email</td>
                      <td className="text-black">
                        {company.primaryContact.email}
                      </td>
                    </tr>
                    <tr>
                      <td>Phone</td>
                      <td className="text-core-dark">
                        {company.primaryContact.phone}
                      </td>
                    </tr>
                  </>
                )}
              </>
            )}

            {isManager && (
              <>
                <tr className="border-t border-grey-100"></tr>
                <tr>
                  <td
                    colSpan={2}
                    className="text-lg font-medium text-core-dark"
                  >
                    SAML Configuration
                  </td>
                </tr>
                {!editingSaml ? (
                  <>
                    <tr>
                      <td>SAML Status</td>
                      <td className="w-full">
                        <span>
                          {company?.enableSaml &&
                          Boolean(company?.samlDomains?.length)
                            ? 'Enabled'
                            : 'Disabled'}
                        </span>
                      </td>
                    </tr>
                    {company?.enableSaml &&
                      Boolean(company?.samlDomains?.length) && (
                        <>
                          <tr>
                            <td>Enforce SAML</td>
                            <td>{company?.enforceSaml ? 'Yes' : 'No'}</td>
                          </tr>
                          <tr>
                            <td>IDP Metadata</td>
                            <td>
                              {company?.idpMetadataUrl
                                ? `URL: ${company.idpMetadataUrl}`
                                : company?.idpMetadataXml
                                ? 'XML Provided'
                                : 'Not configured'}
                            </td>
                          </tr>
                        </>
                      )}
                    <tr>
                      <td>SAML Domains</td>
                      {company?.samlDomains &&
                      company.samlDomains.length > 0 ? (
                        <td>{company.samlDomains.join(', ')}</td>
                      ) : (
                        <td className="italic text-gray-500">
                          Not configured
                          <div className="mt-1 text-xs">
                            Domains are managed by Learnit support
                          </div>
                        </td>
                      )}
                    </tr>
                    <tr>
                      <td>Company ID</td>
                      <td className="w-full">
                        <div className="flex items-center justify-between gap-4 text-grey-700">
                          <div>{user?.company._id}</div>
                          <div className="cursor-pointer">
                            <CopyToClipboard
                              text={user?.company._id ?? ''}
                              onCopy={() => handleCopy()}
                            >
                              <CopyIcon
                                width="1.5rem"
                                height="1.5rem"
                                fill="#4A4A68"
                              />
                            </CopyToClipboard>
                          </div>
                        </div>
                        <div className="mt-1 text-xs italic text-grey-500">
                          Company ID is used in the relay state in your SAML
                          Identity Provider
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>
                        <ButtonRegular
                          className="button-outline"
                          text="EDIT"
                          onClick={() => setEditingSaml(true)}
                          disabled={!Boolean(company?.samlDomains?.length)}
                        />
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr>
                      <td>Enable SAML</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="enableSaml"
                            checked={samlConfig.enableSaml}
                            onChange={(e) =>
                              setSamlConfig({
                                ...samlConfig,
                                enableSaml: e.target.checked,
                                enforceSaml: e.target.checked,
                              })
                            }
                            className="checkbox"
                          />
                          <label htmlFor="enableSaml">
                            Enable SAML authentication
                          </label>
                        </div>
                      </td>
                    </tr>

                    {samlConfig.enableSaml && (
                      <>
                        <tr>
                          <td>Enforce SAML</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="enforceSaml"
                                checked={samlConfig.enforceSaml}
                                onChange={(e) =>
                                  setSamlConfig({
                                    ...samlConfig,
                                    enforceSaml: e.target.checked,
                                  })
                                }
                                disabled={!samlConfig.enableSaml}
                                className="checkbox"
                              />
                              <label htmlFor="enforceSaml">
                                Enforce SAML authentication
                              </label>
                            </div>
                            <div className="mt-1 text-xs italic text-grey-500">
                              Unchecking this gives users the option to sign in
                              with their own password as well as through SAML.
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td>IDP Metadata Source</td>
                          <td className="w-full">
                            <div>
                              <div className="mb-2">URL:</div>
                              <input
                                type="text"
                                value={samlConfig.idpMetadataUrl || ''}
                                onChange={(e) =>
                                  setSamlConfig({
                                    ...samlConfig,
                                    idpMetadataUrl: e.target.value,
                                    // Clear XML if URL is provided
                                    idpMetadataXml: e.target.value
                                      ? ''
                                      : samlConfig.idpMetadataXml,
                                  })
                                }
                                placeholder="https://your-idp.com/metadata"
                                className="w-full p-2 border rounded"
                              />
                            </div>
                            <div>
                              <div className="my-2">OR XML:</div>
                              <textarea
                                value={samlConfig.idpMetadataXml || ''}
                                onChange={(e) =>
                                  setSamlConfig({
                                    ...samlConfig,
                                    idpMetadataXml: e.target.value,
                                    // Clear URL if XML is provided
                                    idpMetadataUrl: e.target.value
                                      ? ''
                                      : samlConfig.idpMetadataUrl,
                                  })
                                }
                                placeholder="Paste XML metadata here"
                                className="w-full h-32 p-2 font-mono text-sm border rounded"
                              />
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td>SAML Domains</td>
                          <td>
                            <div className="italic text-gray-500">
                              {company?.samlDomains &&
                              company.samlDomains.length > 0
                                ? company.samlDomains.join(', ')
                                : 'Not configured'}
                              <div className="mt-1 text-xs">
                                Domains are managed by Learnit support
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Company ID</td>
                          <td>
                            <div className="flex items-center justify-between gap-4 text-grey-700">
                              <div>{user?.company._id}</div>
                              <div className="cursor-pointer">
                                <CopyToClipboard
                                  text={user?.company?._id ?? ''}
                                  onCopy={() => handleCopy()}
                                >
                                  <CopyIcon
                                    width="1.5rem"
                                    height="1.5rem"
                                    fill="#4A4A68"
                                  />
                                </CopyToClipboard>
                              </div>
                            </div>
                            <div className="mt-1 text-xs italic text-grey-500">
                              Company ID is used in the relay state in your SAML
                              Identity Provider
                            </div>
                          </td>
                        </tr>
                      </>
                    )}

                    <tr>
                      <td></td>
                      <td>
                        <div className="flex gap-4 mt-4">
                          <ButtonRegular
                            className="button-primary"
                            text="Save"
                            onClick={() => samlMutation.mutate(samlConfig)}
                            disabled={samlMutation.isPending}
                          />
                          <ButtonRegular
                            className="button-outline"
                            text="Cancel"
                            onClick={() => setEditingSaml(false)}
                            disabled={samlMutation.isPending}
                          />
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
        {isLoading && (
          <ContentLoader width="100%" height="200px" className="rounded-lg">
            <rect x="0" y="0" rx="10" ry="10" width="152" height="100%" />
            <rect x="160" y="0" rx="10" ry="10" width="100%" height="100%" />
          </ContentLoader>
        )}
      </div>
    </>
  );
};

export default CompanySettings;
